/**
 * HollyWarehouseDetailPanel — warehouse details surfaced as a modal (desktop)
 * or bottom sheet (mobile), portaled above the Holly chat panel.
 *
 * Layout:
 *   Desktop (>768px): centered modal, ~600px wide, max-h 85vh. Chat panel
 *                     remains visible behind, dimmed by the modal backdrop.
 *   Mobile (≤768px):  bottom sheet, full-width, max-h 90vh, drag-handle
 *                     indicator at the top. Slides up from the bottom.
 *
 * Dismissal: top-right ×, full-width "Back to chat" footer button, backdrop
 * tap, or Escape (capture phase — beats the chat panel's own Escape).
 *
 * Rendered as a sibling of the chat panel via React portal so it escapes the
 * chat panel's transform-containing-block (slide-in animation creates one).
 *
 * Data: every rich-detail section is rendered only when its field is present.
 * The Holly API currently emits only `id/name/description/location/images/
 * palletStoragePricePerWeek/maxStorageCapacityAvailable`; the richer optional
 * fields on HollyWarehouse (subtitle, rating, temperature, storageType,
 * maxStorageCapacity, specialServices, industryFocus) surface automatically
 * once the backend starts returning them.
 */

import { lazy, Suspense, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useHolly } from './HollyContext';

// Deferred for the same reason as in HollyWarehouseCard: keep the
// ~1.7MB mapbox-gl bundle out of every page's chunk graph.
const HollyWarehouseMap = lazy(() => import('./HollyWarehouseMap'));

function CloseIcon() {
  return (
    <svg
      width='24'
      height='24'
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
      aria-hidden='true'
    >
      <line x1='18' y1='6' x2='6' y2='18' />
      <line x1='6' y1='6' x2='18' y2='18' />
    </svg>
  );
}

/* ── Sub-components ─────────────────────────────────────────────────── */

function InfoCard({ label, value }: { label: string; value: string }) {
  return (
    <div
      style={{
        backgroundColor: '#F3F4F6',
        borderRadius: 8,
        padding: '10px 12px',
      }}
    >
      <div
        style={{
          fontSize: 10,
          fontWeight: 600,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          color: 'rgba(30,30,47,0.55)',
          marginBottom: 4,
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: 14, fontWeight: 600, color: '#1E1E2F' }}>
        {value}
      </div>
    </div>
  );
}

function ChipSection({
  label,
  chips,
  variant,
}: {
  label: string;
  chips: string[];
  variant: 'blue' | 'green';
}) {
  const palette =
    variant === 'blue'
      ? { bg: '#DBEAFE', fg: '#1D4ED8' }
      : { bg: '#DCFCE7', fg: '#15803D' };

  return (
    <div style={{ marginBottom: 16 }}>
      <div
        style={{
          fontSize: 10,
          fontWeight: 600,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          color: 'rgba(30,30,47,0.55)',
          marginBottom: 6,
        }}
      >
        {label}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {chips.map((chip) => (
          <span
            key={chip}
            style={{
              padding: '4px 10px',
              borderRadius: 999,
              fontSize: 12,
              fontWeight: 500,
              backgroundColor: palette.bg,
              color: palette.fg,
            }}
          >
            {chip}
          </span>
        ))}
      </div>
    </div>
  );
}

export default function HollyWarehouseDetailPanel() {
  const { selectedWarehouse, selectedWarehouseLabel, selectWarehouse } =
    useHolly();
  const [mounted, setMounted] = useState(false);

  useEffect(() => setMounted(true), []);

  /* Escape closes the modal — capture phase so we beat the chat panel handler. */
  useEffect(() => {
    if (!selectedWarehouse) return undefined;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        selectWarehouse(null);
      }
    };
    document.addEventListener('keydown', handleKey, { capture: true });
    return () =>
      document.removeEventListener('keydown', handleKey, { capture: true });
  }, [selectedWarehouse, selectWarehouse]);

  if (!selectedWarehouse || !mounted) return null;

  const warehouse = selectedWarehouse;
  const displayLabel = selectedWarehouseLabel ?? 'Warehouse';
  const available = warehouse.maxStorageCapacityAvailable;
  const totalCapacity = warehouse.maxStorageCapacity;
  const close = () => selectWarehouse(null);

  return createPortal(
    <div
      role='dialog'
      aria-modal='true'
      aria-label='Warehouse details'
      className='fixed inset-0 z-[310] flex items-end md:items-center justify-center'
      style={{ fontFamily: "'Poppins', sans-serif" }}
    >
      {/* Backdrop */}
      <div
        aria-hidden='true'
        onClick={close}
        className='holly-animate absolute inset-0 bg-black/40'
        style={{
          animation: 'holly-fade-in var(--duration-normal) var(--ease-out)',
        }}
      />

      {/* Sheet / modal */}
      <div
        data-holly-warehouse-modal
        className='holly-animate relative bg-white flex flex-col w-full md:max-w-[600px] max-h-[90vh] md:max-h-[85vh] rounded-t-[16px] md:rounded-[16px] shadow-2xl'
        style={{
          animation: 'holly-slide-in-up 350ms cubic-bezier(0.16,1,0.3,1)',
        }}
      >
        {/* Desktop override: fade in instead of slide up. */}
        <style>{`
          @media (min-width: 769px) {
            [data-holly-warehouse-modal] {
              animation: holly-fade-in 200ms ease-out !important;
            }
          }
        `}</style>

        {/* Mobile-only drag-handle indicator (visual only, no drag-to-dismiss). */}
        <div
          className='md:hidden flex justify-center pt-2 pb-1'
          aria-hidden='true'
        >
          <div className='w-9 h-1 rounded-full bg-gray-300' />
        </div>

        {/* Top-right close × */}
        <button
          type='button'
          onClick={close}
          aria-label='Close warehouse details'
          className='absolute top-2 right-3 w-8 h-8 flex items-center justify-center text-[#1E1E2F] hover:text-[#000066] transition-colors cursor-pointer rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3574E3]'
        >
          <CloseIcon />
        </button>

        {/* Map — non-interactive preview (no pan/zoom/scroll). */}
        <div className='rounded-[12px] overflow-hidden mx-4 mt-4 flex-shrink-0'>
          <Suspense
            fallback={
              <div className='w-full h-full min-h-[200px] bg-[#F3F4F6] animate-pulse' />
            }
          >
            <HollyWarehouseMap
              warehouse={warehouse}
              height='240px'
              zoom={11}
              circleRadiusPx={90}
            />
          </Suspense>
        </div>

        {/* Scrollable detail body */}
        <div
          className='hide-scrollbar flex-1 overflow-y-auto px-4 py-4'
          style={{ color: '#1E1E2F' }}
        >
          {/* ── Title row: label + subtitle on the left, rating pill on the right ── */}
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 12,
              marginBottom: 16,
            }}
          >
            <div style={{ minWidth: 0 }}>
              <div
                style={{
                  fontSize: 18,
                  fontWeight: 600,
                  color: '#000066',
                  lineHeight: 1.2,
                }}
              >
                {displayLabel}
              </div>
              {warehouse.subtitle && (
                <div
                  style={{
                    fontSize: 13,
                    color: 'rgba(30,30,47,0.6)',
                    marginTop: 4,
                  }}
                >
                  {warehouse.subtitle}
                </div>
              )}
            </div>

            {typeof warehouse.rating === 'number' && (
              <div
                aria-label={`Rating ${warehouse.rating.toFixed(1)} out of 5`}
                style={{
                  flexShrink: 0,
                  padding: '6px 10px',
                  backgroundColor: '#DCFCE7',
                  color: '#15803D',
                  borderRadius: 8,
                  fontSize: 14,
                  fontWeight: 700,
                  display: 'inline-flex',
                  alignItems: 'baseline',
                  gap: 2,
                }}
              >
                {warehouse.rating.toFixed(1)}
                <span style={{ fontSize: 11, fontWeight: 500, opacity: 0.8 }}>
                  /5.0
                </span>
              </div>
            )}
          </div>

          {/* ── Info grid: 2×2 cards (only renders fields that exist) ── */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 8,
              marginBottom: 16,
            }}
          >
            {warehouse.temperature && (
              <InfoCard label='Temperature' value={warehouse.temperature} />
            )}
            {warehouse.storageType && (
              <InfoCard label='Storage Type' value={warehouse.storageType} />
            )}
            {typeof available === 'number' && available > 0 && (
              <InfoCard
                label='Available'
                value={`${available.toLocaleString()} pallets`}
              />
            )}
            {typeof totalCapacity === 'number' && totalCapacity > 0 && (
              <InfoCard
                label='Total Capacity'
                value={`${totalCapacity.toLocaleString()} pallets`}
              />
            )}
          </div>

          {/* ── Special services ── */}
          {warehouse.specialServices &&
            warehouse.specialServices.length > 0 && (
              <ChipSection
                label='Special Services'
                chips={warehouse.specialServices}
                variant='blue'
              />
            )}

          {/* ── Industry focus ── */}
          {warehouse.industryFocus && warehouse.industryFocus.length > 0 && (
            <ChipSection
              label='Industry Focus'
              chips={warehouse.industryFocus}
              variant='green'
            />
          )}

          {/* ── Yellow callout ── */}
          <p
            role='note'
            style={{
              margin: 0,
              fontSize: 13,
              lineHeight: '20px',
              color: '#B45309',
              backgroundColor: '#FEF3C7',
              border: '1px solid #FDE68A',
              borderRadius: 8,
              padding: '10px 12px',
            }}
          >
            Full details including pricing, exact address and provider contact
            are available after registration.
          </p>
        </div>

        {/* Footer CTA — inline-styled to match the widget's brand palette (the
            Flox tailwindComponents Button has a different label/variant API). */}
        <div className='flex-shrink-0 p-4 border-t border-[#E2E8F0]'>
          <button
            type='button'
            onClick={close}
            className='w-full flex items-center justify-center rounded-lg text-white font-medium'
            style={{
              height: 44,
              backgroundColor: '#2563EB',
              fontFamily: "'Poppins', sans-serif",
              fontSize: 15,
            }}
          >
            Back to chat
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
